"use client";

import { c as _c } from "react/compiler-runtime";
import { useChains } from "@opensea/app-kit";
import { useNumberFormatter } from "@opensea/i18n";
import { Flex, FlexCenter, FlexEnd, ItemAvatar, ItemContent, ItemTitle, Media, NumberDisplay, QuantityBadge, QuantityInput, SkeletonBlock, SkeletonLine, SpaceBetween, TABLE_SIZES, TableCell, TableRow, TextBody, TextOverflowTooltip, useTable } from "@opensea/ui-kit";
import { useOnChange } from "@opensea/ui-kit/hooks";
import { bn } from "@opensea/utils";
import { readFragment } from "gql.tada";
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { InlineSwapButton } from "../../../../../components/InlineSwapButton";
import { Item } from "../../../../../components/Item";
import { OfferAmountInput } from "../../../../../components/OfferAmountInput";
import { useSyncFormFields } from "../../../../../hooks/useSyncFormField";
import { calculateOrderTotals } from "../../../../../lib/fees";
import { useBalances } from "../../../../../providers/BalancesProvider";
import { OFFER_MAX_DECIMALS, ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE, ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE, TRAIT_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE, TRAIT_OFFER_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE } from "../../../constants";
import { CreateCollectionOfferFlowTableRowFragment } from "./CreateCollectionOfferFlowTableRow.fragment";
import { CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES } from "./constants";
export const CreateCollectionOfferFlowTableRow = t0 => {
  const $ = _c(116);
  const {
    item: itemKey,
    index,
    context
  } = t0;
  const t = useTranslations("wallet.CreateCollectionOfferFlowTableRow");
  const collection = readFragment(CreateCollectionOfferFlowTableRowFragment, itemKey);
  const {
    getNativeCurrency,
    getOfferCurrency
  } = useChains();
  const {
    control,
    trigger,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] !== control) {
    t1 = {
      control
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    errors
  } = useFormState(t1);
  let t2;
  if ($[2] !== control) {
    t2 = {
      control,
      name: "offers"
    };
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const offers = useWatch(t2);
  const offer = offers[index];
  const format = useNumberFormatter();
  const isReview = context?.isReview ?? false;
  const error = errors.offers?.[index]?.price;
  let t3;
  if ($[4] !== offer) {
    t3 = calculateOrderTotals(offer ? [offer] : []);
    $[4] = offer;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    withoutFees
  } = t3;
  const {
    getBalanceOfCurrencyBySymbol
  } = useBalances();
  const {
    size
  } = useTable();
  const imageSize = TABLE_SIZES[size].image;
  const traitFloorPrice = offer?.traitFloorPrice;
  const floorPrice = traitFloorPrice ?? collection?.floorPrice?.pricePerItem.token.unit;
  const floorSymbol = collection?.floorPrice?.pricePerItem.token.symbol;
  const lowSeverityWarningThreshold = (offer?.trait || offer?.traits && offer.traits.length > 0) && !traitFloorPrice ? TRAIT_OFFER_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE : ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE;
  const highSeverityWarningThreshold = (offer?.trait || offer?.traits && offer.traits.length > 0) && !traitFloorPrice ? TRAIT_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE : ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE;
  const priceWarning = floorPrice && offer?.price && offer.price > 0 && bn(offer.price).gt(bn(floorPrice).times(1 + lowSeverityWarningThreshold).decimalPlaces(OFFER_MAX_DECIMALS)) ? t("priceAboveFloor", {
    type: traitFloorPrice ? t("trait") : t("collection"),
    price: bn(floorPrice).decimalPlaces(OFFER_MAX_DECIMALS).toString(),
    symbol: floorSymbol ?? ""
  }) : undefined;
  let t4;
  if ($[6] !== floorPrice || $[7] !== highSeverityWarningThreshold || $[8] !== offer || $[9] !== priceWarning) {
    t4 = floorPrice !== undefined && Boolean(priceWarning) && offer?.price && bn(offer.price).gt(bn(floorPrice).times(1 + highSeverityWarningThreshold).decimalPlaces(OFFER_MAX_DECIMALS));
    $[6] = floorPrice;
    $[7] = highSeverityWarningThreshold;
    $[8] = offer;
    $[9] = priceWarning;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const showHighSevPriceWarning = t4;
  const t5 = `offers.${index}.shouldConfirmPrice`;
  const t6 = Boolean(showHighSevPriceWarning);
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = [{
      name: t5,
      value: t6
    }];
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== setValue || $[15] !== t7) {
    t8 = {
      fields: t7,
      setValue
    };
    $[14] = setValue;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useSyncFormFields(t8);
  useOnChange(offer?.quantity, trigger);
  if (!collection) {
    let t9;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <CreateCollectionOfferFlowTableRowSkeleton />;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  const offerSymbol = collection.chain.offerCurrency.symbol;
  const balance = getBalanceOfCurrencyBySymbol({
    chainIdentifier: collection.chain.identifier,
    symbol: offerSymbol
  });
  const t9 = offer?.tokenBalance ?? 0;
  let t10;
  if ($[18] !== offer?.price || $[19] !== offer?.quantity || $[20] !== t9) {
    const tokenBalance = bn(t9);
    const totalOfferAmount = (offer?.price ?? 0) * (offer?.quantity || 1);
    t10 = tokenBalance?.lt(totalOfferAmount);
    $[18] = offer?.price;
    $[19] = offer?.quantity;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const hasInsufficientBalance = t10;
  const nativeCurrency = getNativeCurrency(collection.chain.identifier);
  const offerCurrency = getOfferCurrency(collection.chain.identifier);
  const quantityError = errors.offers?.[index]?.quantity;
  let t11;
  if ($[22] !== control || $[23] !== index || $[24] !== isReview || $[25] !== offer?.quantity || $[26] !== quantityError) {
    t11 = isReview ? <QuantityBadge className="ml-1">{offer?.quantity}</QuantityBadge> : <Controller control={control} name={`offers.${index}.quantity`} render={t12 => {
      const {
        field
      } = t12;
      return <QuantityInput className="ml-1" error={Boolean(quantityError)} min={0} onValueChange={field.onChange} size="sm" value={field.value} />;
    }} />;
    $[22] = control;
    $[23] = index;
    $[24] = isReview;
    $[25] = offer?.quantity;
    $[26] = quantityError;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const quantityContent = t11;
  let t12;
  if ($[28] !== quantityError || $[29] !== t) {
    t12 = quantityError && <TextBody color="error-1" size="xs">{quantityError.message || t("notEnoughItems")}</TextBody>;
    $[28] = quantityError;
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const quantityErrorContent = t12;
  const priceInfo = priceWarning && !isReview || error ? <TextBody color={priceWarning && !showHighSevPriceWarning && !error ? "warning" : "error-1"} size="xs">{error ? error.message : priceWarning}</TextBody> : isReview ? null : <TextBody color="text-secondary" size="xs">{t("balance")}{" "}{format(balance?.quantity ?? 0, {
      display: "standard",
      suffix: offerSymbol
    })}</TextBody>;
  let t13;
  if ($[31] !== collection.imageUrl || $[32] !== imageSize) {
    t13 = <ItemAvatar className="rounded" size={imageSize} src={collection.imageUrl} />;
    $[31] = collection.imageUrl;
    $[32] = imageSize;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== collection.name) {
    t14 = <TextOverflowTooltip><ItemTitle>{collection.name}</ItemTitle></TextOverflowTooltip>;
    $[34] = collection.name;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== quantityContent) {
    t15 = <Media greaterThanOrEqual="md">{quantityContent}</Media>;
    $[36] = quantityContent;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <ItemContent><FlexCenter className="gap-2">{t14}{t15}</FlexCenter></ItemContent>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t13 || $[42] !== t16) {
    t17 = <Item variant="unstyled">{t13}{t16}</Item>;
    $[41] = t13;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const collectionLockup = t17;
  let t18;
  if ($[44] !== collection.chain || $[45] !== hasInsufficientBalance || $[46] !== index || $[47] !== isReview || $[48] !== nativeCurrency || $[49] !== offer?.price || $[50] !== offerCurrency || $[51] !== offerSymbol || $[52] !== priceWarning) {
    t18 = isReview ? <TextOverflowTooltip><TextBody><NumberDisplay suffix={offerSymbol} value={offer?.price} /></TextBody></TextOverflowTooltip> : <Flex className="items-center gap-2"><OfferAmountInput autoFocus={index === 0} chain={collection.chain.identifier} offerIndex={index} warning={Boolean(priceWarning)} />{hasInsufficientBalance && <InlineSwapButton initialFromCurrency={{
        symbol: nativeCurrency.symbol,
        contractAddress: nativeCurrency.contractAddress,
        chainIdentifier: collection.chain.identifier
      }} initialToCurrency={{
        symbol: offerCurrency.symbol,
        contractAddress: offerCurrency.contractAddress,
        chainIdentifier: collection.chain.identifier
      }} />}</Flex>;
    $[44] = collection.chain;
    $[45] = hasInsufficientBalance;
    $[46] = index;
    $[47] = isReview;
    $[48] = nativeCurrency;
    $[49] = offer?.price;
    $[50] = offerCurrency;
    $[51] = offerSymbol;
    $[52] = priceWarning;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  const priceContent = t18;
  let t19;
  if ($[54] !== collectionLockup) {
    t19 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}>{collectionLockup}</TableCell>;
    $[54] = collectionLockup;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== quantityContent) {
    t20 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}>{quantityContent}</TableCell>;
    $[56] = quantityContent;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== t19 || $[59] !== t20) {
    t21 = <TableRow>{t19}{t20}</TableRow>;
    $[58] = t19;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t) {
    t22 = t("offerPrice");
    $[61] = t;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t22) {
    t23 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}><TextBody weight="semibold">{t22}</TextBody></TableCell>;
    $[63] = t22;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== priceContent) {
    t24 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}>{priceContent}</TableCell>;
    $[65] = priceContent;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== t23 || $[68] !== t24) {
    t25 = <TableRow>{t23}{t24}</TableRow>;
    $[67] = t23;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== priceInfo) {
    t26 = <FlexEnd>{priceInfo}</FlexEnd>;
    $[70] = priceInfo;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== quantityErrorContent || $[73] !== t21 || $[74] !== t25 || $[75] !== t26) {
    t27 = <Media lessThan="md">{t21}{quantityErrorContent}{t25}{t26}</Media>;
    $[72] = quantityErrorContent;
    $[73] = t21;
    $[74] = t25;
    $[75] = t26;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== collectionLockup) {
    t28 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}>{collectionLockup}</TableCell>;
    $[77] = collectionLockup;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  const t29 = collection.floorPrice?.pricePerItem.token.symbol;
  const t30 = collection.floorPrice?.pricePerItem.token.unit;
  let t31;
  if ($[79] !== t29 || $[80] !== t30) {
    t31 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.floor}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t29} value={t30} /></TextBody></TextOverflowTooltip></TableCell>;
    $[79] = t29;
    $[80] = t30;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== collection.chain || $[83] !== offer) {
    t32 = (offer?.trait || offer?.traits && offer.traits.length > 0) && <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.traitFloor}><TextOverflowTooltip><TextBody><NumberDisplay suffix={collection.chain.listingCurrency.symbol} value={offer?.traitFloorPrice} /></TextBody></TextOverflowTooltip></TableCell>;
    $[82] = collection.chain;
    $[83] = offer;
    $[84] = t32;
  } else {
    t32 = $[84];
  }
  const t33 = collection.topOffer?.pricePerItem.token.symbol;
  const t34 = collection.topOffer?.pricePerItem.token.unit;
  let t35;
  if ($[85] !== t33 || $[86] !== t34) {
    t35 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}><TextOverflowTooltip><TextBody><NumberDisplay suffix={t33} value={t34} /></TextBody></TextOverflowTooltip></TableCell>;
    $[85] = t33;
    $[86] = t34;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== collection.chain || $[89] !== offer) {
    t36 = (offer?.trait || offer?.traits && offer.traits.length > 0) && <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.traitBestOffer}><TextOverflowTooltip><TextBody><NumberDisplay suffix={collection.chain.offerCurrency.symbol} value={offer?.traitTopOfferPrice} /></TextBody></TextOverflowTooltip></TableCell>;
    $[88] = collection.chain;
    $[89] = offer;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== offerSymbol || $[92] !== withoutFees) {
    t37 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}><TextOverflowTooltip><TextBody><NumberDisplay suffix={offerSymbol} value={withoutFees} /></TextBody></TextOverflowTooltip></TableCell>;
    $[91] = offerSymbol;
    $[92] = withoutFees;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  let t38;
  if ($[94] !== priceContent) {
    t38 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}>{priceContent}</TableCell>;
    $[94] = priceContent;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== t28 || $[97] !== t31 || $[98] !== t32 || $[99] !== t35 || $[100] !== t36 || $[101] !== t37 || $[102] !== t38) {
    t39 = <TableRow>{t28}{t31}{t32}{t35}{t36}{t37}{t38}</TableRow>;
    $[96] = t28;
    $[97] = t31;
    $[98] = t32;
    $[99] = t35;
    $[100] = t36;
    $[101] = t37;
    $[102] = t38;
    $[103] = t39;
  } else {
    t39 = $[103];
  }
  let t40;
  if ($[104] !== error || $[105] !== priceInfo || $[106] !== priceWarning || $[107] !== quantityError || $[108] !== quantityErrorContent) {
    t40 = (Boolean(priceWarning) || error || quantityError) && <SpaceBetween className="w-full"><div>{quantityErrorContent}</div><div>{priceInfo}</div></SpaceBetween>;
    $[104] = error;
    $[105] = priceInfo;
    $[106] = priceWarning;
    $[107] = quantityError;
    $[108] = quantityErrorContent;
    $[109] = t40;
  } else {
    t40 = $[109];
  }
  let t41;
  if ($[110] !== t39 || $[111] !== t40) {
    t41 = <Media greaterThanOrEqual="md">{t39}{t40}</Media>;
    $[110] = t39;
    $[111] = t40;
    $[112] = t41;
  } else {
    t41 = $[112];
  }
  let t42;
  if ($[113] !== t27 || $[114] !== t41) {
    t42 = <>{t27}{t41}</>;
    $[113] = t27;
    $[114] = t41;
    $[115] = t42;
  } else {
    t42 = $[115];
  }
  return t42;
};
export const CreateCollectionOfferFlowTableRowSkeleton = () => {
  const $ = _c(12);
  const {
    size
  } = useTable();
  const imageSize = TABLE_SIZES[size].image;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SkeletonBlock />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== imageSize) {
    t1 = <ItemAvatar className="rounded" size={imageSize}>{t0}</ItemAvatar>;
    $[1] = imageSize;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ItemContent><Flex className="gap-2"><SkeletonLine className="h-4 w-16" /></Flex></ItemContent>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}><Item variant="unstyled">{t1}{t2}</Item></TableCell>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.floor}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}><SkeletonLine className="h-4 w-20" /></TableCell>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TableCell className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}><SkeletonLine className="h-8 w-full" /></TableCell>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t3) {
    t8 = <TableRow>{t3}{t4}{t5}{t6}{t7}</TableRow>;
    $[10] = t3;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  return t8;
};